import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { useLocation } from "react-router";
import { RESOURCE_TYPES, formatTimelineResourceData, getTimelineTitleKey } from "constants/index";
import { Col, Row } from "components/Containers";
import { ExpandIcon, ICON_SIZE } from "components/Icon";
import { TimelineEventDataChange } from "components/Timeline";
import { colors, fontSize } from "style";
import { RoleLink } from "components/Missions/Roles";
import { useLinks } from "hooks";
import { MissionLink } from "components/Missions";
import { OrganizationLink } from "components/Organization";

/**
 * TimelineEventTitle
 *
 * @param {String} keyName
 * @param {Object} user
 * @param {Object} impersonator
 * @param {Object} payload
 * @param {String} createdAt
 */
const TimelineEventTitle = ({ keyName, eventType, user: userProp, impersonator, payload, createdAt, profileId }) => {
  const links = useLinks();
  const location = useLocation();
  // TODO: Fix the below check after pivoting profile timeline to eventType
  const isProfileTimeline = location.pathname.includes?.("profile");
  const { resourceId, resourceType, resourceContext } = payload;
  const [showDetails, setShowDetails] = useState(false);
  const userProfileId = parseInt(userProp?.profile?.id);
  const userAffectedOwnProfile = resourceType === RESOURCE_TYPES.profile && resourceId === userProfileId;
  const [titleKey, setKey] = useState(keyName);
  const data = formatTimelineResourceData({
    resourceType,
    resourceContext,
    resourceId,
    profileId,
    userProp,
    impersonator,
    createdAt,
    payload,
    links,
    // Importing components from Missions folder in timelineEvents file gives error
    // So passing the link components as args of formatTimelineResourceData method
    roleLinkComponent: RoleLink,
    missionLinkComponent: MissionLink,
    organizationLinkComponent: OrganizationLink,
  });
  const resourceKeys = ["resourceId", "resourceType", "resourceContext"];
  const payloadKeys = Object.keys(payload);

  useEffect(() => {
    setKey(getTimelineTitleKey(isProfileTimeline ? keyName : eventType, payload, userProp));
  }, [keyName, payload, location, userProp, eventType, isProfileTimeline]);

  return (
    <>
      <FormattedMessage
        id={`Timeline.TimelineEvent.${userAffectedOwnProfile ? "Member." : ""}${titleKey}`}
        values={{
          ...data,
          details: (
            <StyledRow gap="0">
              <ExpandIcon isExpanded={showDetails} size={ICON_SIZE.small} />
              <ShowMoreLink onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? "Hide" : "Show"} details
              </ShowMoreLink>
            </StyledRow>
          ),
        }}
      />
      {showDetails && (
        <StyledCol gap="0.25rem">
          {payloadKeys
            .filter((item) => resourceKeys.indexOf(item) === -1)
            .map((item) => (
              <TimelineEventDataChange key={item} payload={payload} item={item} />
            ))}
        </StyledCol>
      )}
    </>
  );
};

const ShowMoreLink = styled.span`
  font-size: ${fontSize.xxsmall};
  color: ${colors.grayAnatomyLight1};
  cursor: pointer;
`;

const StyledRow = styled(Row)`
  display: inline-flex;
  margin-left: 1rem;
`;

const StyledCol = styled(Col)`
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding-left: 1rem;
  border-left: 2px ${colors.grayAnatomyLight5} solid;
`;

TimelineEventTitle.propTypes = {
  keyName: PropTypes.string.isRequired,
  eventType: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  impersonator: PropTypes.object,
  payload: PropTypes.object.isRequired,
  createdAt: PropTypes.string.isRequired,
  profileId: PropTypes.string,
};

export default TimelineEventTitle;
